import { redactQueryParams } from '@aimfora/config/redact'
import type { Breadcrumb, BreadcrumbHint, ErrorEvent, EventHint } from '@sentry/node'

/** Remove the query string and fragment: links carry one-time tokens (`?token=`). */
export function stripQuery(url: string): string {
  const cut = url.search(/[?#]/)
  return cut === -1 ? url : url.slice(0, cut)
}

interface Scrubbable {
  message?: string
  request?: {
    url?: string
    data?: unknown
    cookies?: unknown
    headers?: unknown
    query_string?: unknown
  }
  user?: Record<string, unknown>
  exception?: { values?: { value?: string }[] }
  breadcrumbs?: { message?: string; data?: Record<string, unknown> }[]
}

/**
 * Make an error event safe to send to GlitchTip: never the request body, cookies or headers
 * (they hold passwords and session tokens), no query strings (they hold one-time links), no
 * values from failed queries and no personal details about the user. The event keeps the stack
 * trace, which is what we need.
 */
export function scrubEvent<T extends Scrubbable>(event: T): T {
  if (event.request) {
    event.request.data = undefined
    event.request.cookies = undefined
    event.request.headers = undefined
    event.request.query_string = undefined
    if (event.request.url) event.request.url = stripQuery(event.request.url)
  }
  if (event.user) event.user = event.user.id ? { id: event.user.id } : undefined
  if (event.message) event.message = redactQueryParams(event.message)
  for (const exception of event.exception?.values ?? []) {
    if (exception.value) exception.value = redactQueryParams(exception.value)
  }
  for (const crumb of event.breadcrumbs ?? []) {
    if (crumb.message) crumb.message = redactQueryParams(crumb.message)
    if (!crumb.data) continue
    for (const key of ['url', 'from', 'to']) {
      const value = crumb.data[key]
      if (typeof value === 'string') crumb.data[key] = stripQuery(value)
    }
  }
  return event
}

export const beforeSend = (event: ErrorEvent, _hint?: EventHint) => scrubEvent(event)

/**
 * Click and key-press breadcrumbs describe the element, including its `aria-label`, `title` and
 * `name`. In the upload dialogs those can hold a file name, which is often the student's real
 * name (ADR 0006), so they are not kept at all.
 */
export const beforeBreadcrumb = (crumb: Breadcrumb, _hint?: BreadcrumbHint): Breadcrumb | null =>
  crumb.category?.startsWith('ui.') ? null : crumb
